/*
 * How data gets INTO the sandbox — and the reason it cannot get in any other way.
 *
 * The sandbox has no network, so it cannot fetch its own rows. Everything it
 * analyses is resolved HERE, on the host, before the worker is told anything, and
 * handed across as bytes. Two properties fall out of that, and they are the two
 * that matter most about this feature:
 *
 * HORIZONTAL. A source is one function that returns columns. Integrations, an
 * MCP tool's result, a model's variables, a file somebody dropped in the chat —
 * each is a resolver, and the sandbox does not change to accept it. It never
 * learns where a table came from, and it must not: the day it does, adding a
 * source stops being additive.
 *
 * SAFE, by the same mechanism rather than by a second one. Every resolver goes
 * through the same backend API, on the same session, as the tool the agent
 * already holds — so the sandbox cannot reach anything its caller could not reach
 * by asking directly. There is no new data path to authorize and no second place
 * for org scoping to be got wrong. A capability the caller lacks is a dataset
 * that fails to resolve, in exactly the way the underlying call fails.
 *
 * WHAT IS DELIBERATELY DIFFERENT from the agent-facing tools is the ROW CAP, and
 * only the row cap. `history.query` returns at most 200 points because a model
 * cannot read more than that and should not be handed a spreadsheet to be a
 * chart. The sandbox is not a model: it can take a hundred thousand rows and
 * return one number, which is the entire argument for its existence. Same API,
 * same session, same scoping, different consumer.
 */
import type { BackendClient } from '../main/bridge/backend-client';
import { inFlightLimit } from '../core/bridge/history-typed';
import type { Dataset } from './protocol';

/**
 * The most rows one dataset may carry.
 *
 * Bounded because these are transferred into a worker with a finite heap and
 * because an unbounded resolver turns a typo into a browser tab that dies. A
 * million float64s is 8 MB per column, which is a big-but-survivable table; past
 * that the honest answer is a coarser bucket, and the model is told so.
 */
export const MAX_ROWS = 1_000_000;

/**
 * The most rows ONE RUN may carry, across all of its datasets.
 *
 * Rows, not datasets, are what cost the worker memory, so the run is bounded by
 * them: twenty short series cost less than one long one. Two million is about
 * 34 MB of ts + value + quality before pandas copies it, which a sandbox worker
 * holds comfortably and the pool's four of them still do.
 */
export const TOTAL_ROW_BUDGET = 2_000_000;

/**
 * How many datasets are fetched at once.
 *
 * Four, for the reasons the typed history reader settled on four
 * (`TYPED_SYMBOLS_IN_FLIGHT`): the browser opens at most six connections to one
 * origin and the History tab and live values need some of them, and the
 * historian's admission slots are shared by everyone reading that gateway.
 */
export const DATASETS_IN_FLIGHT = 4;

/**
 * A runaway guard on how many datasets one run may name, not a policy.
 *
 * The policy is the row budget. This exists so a model that loops does not queue
 * a thousand requests against a gateway other people are reading from.
 */
export const MAX_DATASETS = 50;

/**
 * How long one run may spend loading its datasets, in ms.
 *
 * A raw read is pages one after another, ~0.66 s each on a bench (half of it in
 * the historian, half in the hops around it), so a long thirty-hertz window
 * reaches the worker's 60 s fuse before its million-row share — and the fuse
 * threw away every row already read. At this deadline a read stops and keeps
 * what it has, marked as cut by time. 45 s leaves the page in flight its
 * gateway deadline (10 s) under the fuse. One budget for the whole run, not
 * one per dataset: the person waits for the run, not for each dataset.
 *
 * Running reads side by side does not buy more of it. With four raw reads in
 * flight on a bench, the historian's own work per page rose from ~0.43 s to
 * ~1.5 s and nothing queued: its throughput (~15k rows/s there) is shared, not
 * multiplied. So how the time is shared decides who gets data at all — see
 * resolveDatasets.
 */
export const LOAD_BUDGET_MS = 45_000;

/** A dataset the model asked for, before it has been resolved. */
export type DatasetRequest =
  | {
      source: 'history';
      name: string;
      gatewayId: string;
      controllerId: string;
      symbolPath: string;
      fromMs: number;
      toMs: number;
      /** null for raw samples; seconds per bucket for an envelope. */
      bucketSeconds: number | null;
      /** A model's state changes (`$events.state_transition` under `model:<mid>`), read as a table
       *  of ts, from, to and event rather than as one column of JSON. Raw only. */
      transitions?: true;
    }
  | {
      /** A target inside one of the org's integrations — a table in their
       *  database, a collection, a named path. The second integration API, read
       *  through the same paged RPC the data grid uses. */
      source: 'integration';
      name: string;
      gatewayId: string;
      integrationId: string;
      target: string;
      /** Stop after this many rows. Bounded by MAX_ROWS regardless. */
      maxRows?: number;
    }
  | {
      /** Rows the model supplies itself — a lookup table, a set of thresholds,
       *  something it worked out in a previous step and wants to join against.
       *
       *  This is also the honest answer for an MCP tool's result and for anything
       *  else the agent can already obtain as JSON: it calls the tool, then hands
       *  the rows here. A resolver for MCP would be a second way to make a call
       *  the agent can already make, with a second place for its scoping to be
       *  got wrong. */
      source: 'inline';
      name: string;
      rows: Record<string, unknown>[];
    }
  | {
      /** A CSV or Excel file in this conversation — attached by a person, or written by an earlier
       *  run — named as the conversation names it. Read whole: a chat attachment is at most 10 MB. */
      source: 'file';
      name: string;
      file: string;
      format: 'csv' | 'xlsx';
      /** Excel only: the sheet to read; the first when absent. */
      sheet?: string;
    };

/** A python identifier, because the name becomes one. Rejected rather than
 *  mangled: a model that asked for `my data` and got `my_data` will spend its
 *  next step confused about a NameError. */
export const NAME_OK = /^[A-Za-z_][A-Za-z0-9_]*$/;
/** Names a dataset may not take: globals the sandbox itself puts there. */
export const RESERVED_NAMES = new Set(['vitesse']);

export interface ResolveDeps {
  backend(): BackendClient | undefined;
  /** The files of the conversation this run belongs to. Absent where there is none to read. */
  files?: ConversationFiles;
}

/** A conversation's files, through the AI plane on the caller's session — the same read the
 *  transcript's download chips make. */
export interface ConversationFiles {
  /** Every file part the conversation holds, oldest first. */
  list(): Promise<{ sha256: string; filename: string; mediaType: string }[]>;
  read(sha256: string): Promise<Uint8Array | null>;
}

/** Resolve one request into columns. Throws with a sentence the model can act
 *  on — the caller turns it into a tool failure.
 *
 *  `maxRows` is this dataset's share of the run's budget (see `resolveDatasets`);
 *  it defaults to the per-dataset ceiling, which is what a lone dataset gets. */
export async function resolveDataset(deps: ResolveDeps, req: DatasetRequest, maxRows = MAX_ROWS, deadlineMs?: number): Promise<Dataset> {
  if (!NAME_OK.test(req.name)) {
    throw new Error(`"${req.name}" is not usable as a variable name — use letters, digits and underscores, starting with a letter.`);
  }
  // The house helpers' module is a global of that name; a dataset bound over it would take vitesse.excel away mid-conversation.
  if (RESERVED_NAMES.has(req.name)) {
    throw new Error(`"${req.name}" is reserved for the sandbox's own helpers — give the dataset another name.`);
  }
  const cap = Math.min(Math.max(Math.floor(maxRows), 1), MAX_ROWS);
  if (req.source === 'inline') return fromInline(req.name, req.rows, cap);
  if (req.source === 'integration') return fromIntegration(deps, req, cap);
  if (req.source === 'file') return fromFile(deps, req);
  return fromHistory(deps, req, cap, deadlineMs);
}

/**
 * A FILE IN THIS CONVERSATION, handed over whole for pandas to read in the sandbox.
 *
 * Found by its file name, the way the conversation shows it, and the latest of that name when there
 * are several — a person who attaches a corrected export means the new one. Not parsed here: a
 * CSV's separator and encoding, and an Excel file's sheets, are pandas's to understand, so its
 * columns are empty and its rows unknown until the sandbox has read it (`RunResult.rows`).
 */
async function fromFile(deps: ResolveDeps, req: Extract<DatasetRequest, { source: 'file' }>): Promise<Dataset> {
  if (!deps.files) throw new Error('This run has no conversation to read a file from.');
  const files = await deps.files.list();
  const wanted = req.file.toLowerCase();
  const hit = [...files].reverse().find((f) => f.filename.toLowerCase() === wanted);
  if (!hit) {
    const tables = [...new Set(files.map((f) => f.filename).filter((n) => fileFormat(n) !== null))];
    throw new Error(
      `No file "${req.file}" in this conversation. ${tables.length ? `Its CSV and Excel files are: ${tables.join(', ')}.` : 'It holds no CSV or Excel file.'}`,
    );
  }
  const bytes = await deps.files.read(hit.sha256);
  if (!bytes) throw new Error(`"${hit.filename}" is in this conversation but could not be read.`);
  return {
    name: req.name,
    columns: [],
    rows: -1,
    source: `file · ${hit.filename}${req.sheet ? ` · sheet ${req.sheet}` : ''}`,
    file: { filename: hit.filename, format: req.format, ...(req.sheet ? { sheet: req.sheet } : {}), bytes: bytes.slice().buffer as ArrayBuffer },
  };
}

/** What a file is read as, from its name: the two the sandbox reads, or null. */
export function fileFormat(filename: string): 'csv' | 'xlsx' | null {
  const ext = filename.toLowerCase().split('.').pop();
  return ext === 'csv' ? 'csv' : ext === 'xlsx' ? 'xlsx' : null;
}

/** A dataset that could not be loaded, carrying WHICH one, so the sentence the
 *  model reads names the dataset to fix rather than leaving it to guess. */
export class DatasetLoadError extends Error {
  constructor(
    readonly dataset: string,
    message: string,
  ) {
    super(message);
    this.name = 'DatasetLoadError';
  }
}

/**
 * Resolve every dataset a run names: in parallel, inside one row budget and one load budget.
 *
 * IN PARALLEL, because one round trip per dataset in sequence was what a run
 * paid before (eight datasets of forty rows took five to seventeen seconds on a
 * bench, nearly all of it waiting), and every step the model spends getting its
 * data loaded is a step out of a small budget it needs for the analysis.
 *
 * ONE ROW BUDGET, SHARED EQUALLY. Each dataset may carry `TOTAL_ROW_BUDGET / n`
 * rows, capped at `MAX_ROWS`. Equal shares are decided before anything is
 * fetched, so the memory a run can hold is bounded while it is loading, not
 * only after. The cost is that one long series beside many short ones gets no
 * more than its share; it is marked truncated and the model is told, which is
 * the honest outcome when the run asked for more than one budget holds.
 *
 * ONE LOAD DEADLINE (`LOAD_BUDGET_MS`), and RAW HISTORY READS TAKE TURNS BY
 * THE PAGE. Every raw read starts at once and is handed the run's deadline;
 * the reader queues each page for one of a few permits (`permitPool`, in the
 * backend worker), so what is bounded is what is scarce — pages in flight — and
 * the time is shared in turn whatever order the datasets were named in. A read
 * that reaches the deadline keeps what it read, marked as cut by time. Other
 * sources are one request or a short paged read each and keep the plain limit
 * of `DATASETS_IN_FLIGHT` at a time.
 *
 * ORDER IS THE REQUEST'S, whatever order the fetches finish in, so a dataset is
 * always bound under the name and in the position the model asked for.
 *
 * ONE FAILURE FAILS THE RUN, at once, and no dataset waiting for its turn under
 * the plain limit is started: a run whose data did not all load would reach
 * the interpreter as a NameError and send the model hunting through correct
 * code. Reads already started run on — an AbortSignal cannot cross into the
 * backend worker over Comlink — but stop at the deadline, and their results
 * are dropped.
 */
export async function resolveDatasets(
  deps: ResolveDeps,
  requests: readonly DatasetRequest[],
  opts: { inFlight?: number; budget?: number; loadBudgetMs?: number; now?: () => number } = {},
): Promise<Dataset[]> {
  if (requests.length === 0) return [];
  const share = Math.max(1, Math.floor((opts.budget ?? TOTAL_ROW_BUDGET) / requests.length));
  const deadlineMs = (opts.now ?? Date.now)() + (opts.loadBudgetMs ?? LOAD_BUDGET_MS);
  const out: Dataset[] = new Array(requests.length);
  const failed = new AbortController();
  const load = async (i: number): Promise<void> => {
    const req = requests[i]!;
    const t0 = performance.now();
    try {
      const d = await resolveDataset(deps, req, share, deadlineMs);
      // How long each dataset took, kept beside it for the transcript and the audit. Without it a
      // slow run could not be told apart into "many datasets" and "one slow historian read".
      out[i] = { ...d, loadMs: Math.round(performance.now() - t0) };
    } catch (e) {
      failed.abort();
      throw new DatasetLoadError(req.name, e instanceof Error ? e.message : String(e));
    }
  };
  const raw: number[] = [];
  const rest: number[] = [];
  requests.forEach((r, i) => (r.source === 'history' && !r.bucketSeconds ? raw : rest).push(i));
  await Promise.all([...raw.map(load), inFlightLimit(rest, opts.inFlight ?? DATASETS_IN_FLIGHT, load, failed.signal)]);
  return out;
}

/** Rows the model supplied, held to the same share as any other dataset. */
function fromInline(name: string, rows: Record<string, unknown>[], cap: number): Dataset {
  if (rows.length <= cap) return fromRows(name, rows, 'supplied inline');
  return { ...fromRows(name, rows.slice(0, cap), 'supplied inline'), truncated: true, truncatedBy: 'rows' };
}

/**
 * A whole integration target, paged in.
 *
 * THE ROW CAP IS THE ONLY THING THAT DIFFERS from `integrations.read`, which is
 * the same call with a ceiling of fifty. That tool exists so a model can SEE a
 * table; this exists so code can COMPUTE over one, and the distinction is the
 * whole argument for the sandbox — the same rows, the same RPC, the same
 * session, a consumer that can take a hundred thousand of them and return one
 * number.
 *
 * PAGED WITH THE SERVER'S OWN CURSOR rather than one large request. Keyset
 * paging is what makes the underlying read O(log n) at any depth, so a big table
 * arrives as a stream of bounded pages instead of one request the source has to
 * hold in memory; and it means the cap is enforced by stopping, not by asking
 * for a million rows and hoping.
 */
async function fromIntegration(deps: ResolveDeps, req: Extract<DatasetRequest, { source: 'integration' }>, cap: number): Promise<Dataset> {
  const backend = deps.backend();
  if (!backend) throw new Error('The backend is not ready yet — try again in a moment.');

  const want = Math.min(Math.max(Math.floor(req.maxRows ?? cap), 1), cap);
  const rows: Record<string, unknown>[] = [];
  let cursor = '';
  let truncated = false;
  /*
   * A PAGE BUDGET, not just a row budget.
   *
   * A source that returns a non-empty page and always hands back the same cursor
   * would otherwise spin here forever. The row cap alone does not close that: a
   * page of one row, repeated, reaches a million requests before it reaches a
   * million rows. So the loop is bounded from both ends and the cursor is
   * required to MOVE.
   */
  const PAGE = 5_000;
  const maxPages = Math.ceil(want / PAGE) + 4;
  for (let page = 0; page < maxPages; page += 1) {
    const got = await backend.api.integrationReadRows(req.gatewayId, req.integrationId, req.target, cursor, Math.min(PAGE, want - rows.length));
    for (const r of got.rows) rows.push(r);
    if (rows.length >= want) {
      // The SERVER's cursor is the authority on whether more exists — not the
      // page size, which a source is free to under-fill.
      truncated = got.nextCursor !== '';
      break;
    }
    if (!got.nextCursor || got.nextCursor === cursor) break; // the end, or a source that will not advance
    cursor = got.nextCursor;
  }
  if (rows.length > want) rows.length = want;

  const ds = fromRows(req.name, rows, `integration ${req.integrationId} · ${req.target}`);
  return truncated ? { ...ds, truncated: true, truncatedBy: 'rows' } : ds;
}

async function fromHistory(deps: ResolveDeps, req: Extract<DatasetRequest, { source: 'history' }>, cap: number, deadlineMs?: number): Promise<Dataset> {
  const backend = deps.backend();
  if (!backend) throw new Error('The backend is not ready yet — try again in a moment.');
  if (req.transitions) return fromTransitions(backend, req, cap, deadlineMs);
  return req.bucketSeconds ? fromHistoryBuckets(backend, req, req.bucketSeconds, cap) : fromHistoryRaw(backend, req, cap, deadlineMs);
}

const iso = (ms: number): string => new Date(ms).toISOString();

/**
 * Raw samples, read as COLUMNS by the backend worker and handed over without a copy.
 *
 * Not through `historyQuery`, which is the History tab's chart client: it turned
 * booleans into 0/1, dropped strings and quality, and stopped after twenty pages
 * (two hundred thousand rows) of a window without saying so — so a long window
 * arrived as its EARLIEST part, unflagged. The columnar reader keeps every
 * sample's type and quality, reads up to this dataset's share of the budget, and
 * reports exactly which part of the window it holds.
 *
 * `ts` goes on as the historian's nanoseconds, marked as time, so Python gets a
 * UTC timestamp at full precision: two samples microseconds apart stay two
 * distinct instants rather than collapsing onto one millisecond.
 */
async function fromHistoryRaw(
  backend: BackendClient,
  req: Extract<DatasetRequest, { source: 'history' }>,
  cap: number,
  deadlineMs?: number,
): Promise<Dataset> {
  const c = await backend.api.historyColumns(req.gatewayId, req.controllerId, req.symbolPath, req.fromMs, req.toMs, cap, deadlineMs);
  const window = { fromMs: Number(c.coveredFromNs / 1_000_000n), toMs: Number(c.coveredToNs / 1_000_000n) };
  /*
   * A CUT READ HOLDS THE START OF THE WINDOW, and says where it stops. The
   * historian pages forward from the start, so there is no "most recent half" to
   * keep; what keeps the reader honest is naming the part it has.
   */
  const span = c.truncated ? `${iso(window.fromMs)} → ${iso(window.toMs)} of ${iso(req.fromMs)} → ${iso(req.toMs)}` : `${iso(req.fromMs)} → ${iso(req.toMs)}`;
  return {
    name: req.name,
    columns: [
      { name: 'ts', values: c.tsNs, time: true },
      { name: 'value', values: c.value },
      { name: 'quality', values: c.quality },
    ],
    rows: c.rows,
    source: `historian · ${req.symbolPath} on ${req.controllerId} · ${span} · raw${c.notes.length ? ` · ${c.notes.join(' ')}` : ''}`,
    window,
    asked: { fromMs: req.fromMs, toMs: req.toMs },
    // A symbol's rows come back in time order — the cursor paging depends on it — so the ends are the span.
    ...(c.rows ? { samples: { firstMs: Number(c.tsNs[0]! / 1_000_000n), lastMs: Number(c.tsNs[c.rows - 1]! / 1_000_000n) } } : {}),
    ...(c.notes.length ? { notes: c.notes } : {}),
    ...(c.truncated ? { truncated: true, truncatedBy: c.truncatedBy ?? 'historian' } : {}),
  };
}

/**
 * A MODEL'S STATE CHANGES, as the table an availability is computed from.
 *
 * The historian keeps each change as one JSON string, `{"from":[…],"to":[…],"event":"…"}`, under
 * the model's `$events.state_transition` (ingest.go). Read raw like any other series, then split
 * into columns, so the model groups by state instead of parsing JSON in every run.
 *
 * `to` stays a LIST: it is every state active after the change, each active leaf followed by its
 * parents (both engines report it so), so a nested or parallel model is in several states at once
 * and the time in a state is the time it is in `to`. `from` is the one state the change left —
 * missing on the change that started the model, which the engine reports from a generated id, or
 * from nothing when it resumed. `event` is missing when the engine did not report one: on that
 * start, and on a step a timer fired.
 *
 * A row that is not a state change is left out and counted in the notes rather than dropped
 * silently or failing the run.
 */
async function fromTransitions(
  backend: BackendClient,
  req: Extract<DatasetRequest, { source: 'history' }>,
  cap: number,
  deadlineMs?: number,
): Promise<Dataset> {
  const raw = await fromHistoryRaw(backend, req, cap, deadlineMs);
  const ts = raw.columns[0]!.values as BigInt64Array;
  const values = raw.columns[1]!.values as ArrayLike<unknown>;
  const kept: bigint[] = [];
  const from: (string | null)[] = [];
  const to: (string[] | null)[] = [];
  const event: (string | null)[] = [];
  let unreadable = 0;
  for (let i = 0; i < raw.rows; i += 1) {
    let d: { from?: unknown; to?: unknown; event?: unknown } | null = null;
    try {
      d = typeof values[i] === 'string' ? JSON.parse(values[i] as string) : null;
    } catch {
      d = null;
    }
    if (!d || !Array.isArray(d.to)) {
      unreadable += 1;
      continue;
    }
    kept.push(ts[i]!);
    const left = Array.isArray(d.from) && typeof d.from[0] === 'string' ? d.from[0] : '';
    from.push(left && !left.startsWith('$generated') ? left : null);
    to.push(d.to.filter((x): x is string => typeof x === 'string'));
    event.push(typeof d.event === 'string' && d.event ? d.event : null);
  }
  const tsKept = BigInt64Array.from(kept);
  const notes = [...(raw.notes ?? []), ...(unreadable ? [`${unreadable} row${unreadable === 1 ? ' was' : 's were'} not a state change and left out.`] : [])];
  return {
    ...raw,
    columns: [
      { name: 'ts', values: tsKept, time: true },
      { name: 'from', values: from },
      { name: 'to', values: to },
      { name: 'event', values: event },
    ],
    rows: tsKept.length,
    ...(tsKept.length ? { samples: { firstMs: Number(tsKept[0]! / 1_000_000n), lastMs: Number(tsKept[tsKept.length - 1]! / 1_000_000n) } } : { samples: undefined }),
    ...(notes.length ? { notes } : {}),
  };
}

/** Bucketed min/max/avg — numbers by construction, and few of them: one row per bucket. */
async function fromHistoryBuckets(
  backend: BackendClient,
  req: Extract<DatasetRequest, { source: 'history' }>,
  bucketSeconds: number,
  cap: number,
): Promise<Dataset> {
  const buckets = await backend.api.historyQuery(req.gatewayId, req.controllerId, req.symbolPath, req.fromMs, req.toMs, bucketSeconds);

  // The envelope arrives whole, so here there IS a choice of which part to keep:
  // the most recent, as history.query does, because a window missing its newest
  // buckets is the wrong half for almost every question anyone asks of it.
  const truncated = buckets.length > cap;
  const rows = truncated ? buckets.slice(-cap) : buckets;

  const ts = new BigInt64Array(rows.length);
  const value = new Float64Array(rows.length);
  const min = new Float64Array(rows.length);
  const max = new Float64Array(rows.length);
  const count = new Float64Array(rows.length);
  for (let i = 0; i < rows.length; i += 1) {
    const b = rows[i]!;
    ts[i] = BigInt(Math.round(b.tsMs)) * 1_000_000n;
    value[i] = b.avg;
    min[i] = b.min;
    max[i] = b.max;
    count[i] = b.sampleCount;
  }

  /*
   * RAW AND AGGREGATED GET DIFFERENT COLUMNS, because they are different data
   * and one shape for both would lie. A raw sample has no min/max/count — the
   * historian fills them with the value itself — and a frame carrying three
   * identical columns invites a model to compute a spread that is always zero.
   */
  const window = truncated && rows.length ? { fromMs: rows[0]!.tsMs, toMs: req.toMs } : { fromMs: req.fromMs, toMs: req.toMs };
  return {
    name: req.name,
    columns: [
      { name: 'ts', values: ts, time: true },
      { name: 'avg', values: value },
      { name: 'min', values: min },
      { name: 'max', values: max },
      { name: 'samples', values: count },
    ],
    rows: rows.length,
    source: `historian · ${req.symbolPath} on ${req.controllerId} · ${iso(window.fromMs)} → ${iso(window.toMs)} · ${bucketSeconds}s buckets`,
    window,
    asked: { fromMs: req.fromMs, toMs: req.toMs },
    ...(rows.length ? { samples: { firstMs: rows[0]!.tsMs, lastMs: rows[rows.length - 1]!.tsMs } } : {}),
    ...(truncated ? { truncated: true, truncatedBy: 'rows' as const } : {}),
  };
}

/**
 * Columns from an array of objects.
 *
 * The column set is the union of every row's keys, not the first row's: a
 * resolver that took the first row's shape would silently drop a field that only
 * appears later, which is the failure mode nobody notices until a number is
 * wrong. A row missing a key gets a null.
 *
 * Typed where the column is entirely numeric, plain arrays otherwise — a mixed
 * column is a string column, because coercing it would invent values.
 */
export function fromRows(name: string, rows: Record<string, unknown>[], source: string): Dataset {
  const keys: string[] = [];
  const seen = new Set<string>();
  for (const r of rows) for (const k of Object.keys(r)) if (!seen.has(k)) { seen.add(k); keys.push(k); }

  const columns = keys.map((key) => {
    const raw = rows.map((r) => (key in r ? r[key] : null));
    const numeric = raw.every((v) => v === null || v === undefined || typeof v === 'number' || typeof v === 'boolean');
    if (numeric) {
      const arr = new Float64Array(raw.length);
      for (let i = 0; i < raw.length; i += 1) {
        const v = raw[i];
        arr[i] = v === null || v === undefined ? Number.NaN : Number(v);
      }
      return { name: key, values: arr };
    }
    return { name: key, values: raw.map((v) => (v === null || v === undefined ? null : String(v))) };
  });

  return { name, columns, rows: rows.length, source };
}
